import type { BaseSyntheticEvent, FC, FormHTMLAttributes } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { FieldValues, Mode, UseFormReturn } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

// biome-ignore lint: any is needed, because we don't the data type
export type FormSubmit<TData = any> = (
  data: TData,
  methods: UseFormReturn,
) => Promise<void>;

export type Props = {
  className?: string;
  defaultValues?: FieldValues;
  mode?: Mode;
  onSubmit: FormSubmit;
} & Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit' | 'className'>;

const Form: FC<Props> = ({
  className,
  onSubmit,
  children,
  defaultValues,
  mode,
  ...props
}) => {
  const methods = useForm({ defaultValues, mode });
  const submitPromise = useRef<Promise<void> | undefined>(undefined);

  const submit = useCallback(
    async (e: BaseSyntheticEvent) => {
      e.preventDefault();

      if (methods) {
        if (methods.formState?.isSubmitting && submitPromise.current)
          return submitPromise.current;

        submitPromise.current = methods.handleSubmit(async (data) => {
          try {
            await onSubmit(data, methods);
          } catch (e) {
            Object.entries(e as Record<string, string>).forEach(
              ([name, message]) => {
                methods.setError(name, {
                  type: 'manual',
                  message,
                });
              },
            );
          }
        })(e);

        submitPromise.current.finally(() => {
          submitPromise.current = undefined;
        });

        return submitPromise.current;
      }
    },
    [onSubmit, methods],
  );

  useEffect(() => {
    if (methods) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods]);

  return (
    methods && (
      <FormProvider {...methods}>
        <form className={className} onSubmit={submit} noValidate {...props}>
          {children}
        </form>
      </FormProvider>
    )
  );
};
export default Form;
